import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { useEffect, useState } from 'react'

import { Link } from '@/components/nextui'
import { useAuthHook, useIcfes } from '@/hooks'
import { AspirantIcfesExam } from '@/types/icfes'
import {
    MEET_US_EDUCATION_PAGE,
    navigateToMeetUsPage
} from '@/utils/meetUsNavigation'

// sap_document_subtest del certificado de resultados ICFES (Saber 11)
export const ICFES_RESULTS_SUBTEST_ID = 5

const FOREIGN_EXAM_CODE = '06'

// Examen más reciente con fecha obligatoria (excluye examen extranjero)
const pickDatedExam = (
    exams: AspirantIcfesExam[]
): AspirantIcfesExam | null => {
    const dated = exams.filter(
        (e) => String(e.icfesTypeId).padStart(2, '0') !== FOREIGN_EXAM_CODE
    )
    const ts = (e: AspirantIcfesExam): number =>
        Date.parse(e.updatedAt || e.createdAt || '') || 0

    return dated.sort((a, b) => ts(b) - ts(a))[0] || null
}

// Soporta 'dd/MM/yyyy' y 'yyyy-MM-dd' (con o sin hora)
const formatExamDate = (value?: string | null): string | null => {
    if (!value) {
        return null
    }
    const [y, m, d] = value.includes('/')
        ? value.split('/').reverse().map(Number)
        : value.slice(0, 10).split('-').map(Number)
    const date = new Date(y, m - 1, d)

    return Number.isNaN(date.getTime())
        ? value
        : format(date, 'PPP', { locale: es })
}

// Link a Conozcámonos página 4 (Educación), donde se editan los datos ICFES
const EducationPageLink = (): JSX.Element => (
    <Link
        as="button"
        type="button"
        size="sm"
        underline="always"
        className="text-xs text-primary"
        onPress={() => navigateToMeetUsPage(MEET_US_EDUCATION_PAGE)}
    >
        Ir a Conozcámonos, página 4 (Educación)
    </Link>
)

// Datos ICFES registrados en Educación, mostrados en la tarjeta del certificado
export const IcfesDocumentInfo = (): JSX.Element | null => {
    const { idAspirant } = useAuthHook()
    const { getAspirantIcfes } = useIcfes()
    const [exam, setExam] = useState<AspirantIcfesExam | null>(null)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        if (!idAspirant) {
            return
        }
        let active = true

        void getAspirantIcfes(idAspirant).then((exams) => {
            if (active) {
                setExam(pickDatedExam(exams))
                setLoaded(true)
            }
        })

        return () => {
            active = false
        }
        // getAspirantIcfes se recrea en cada render; basta con el aspirante
    }, [idAspirant])

    if (!loaded) {
        return null
    }

    const examDate = formatExamDate(exam?.fechaIcfes)

    if (!examDate) {
        return (
            <div className="mb-3 p-2 rounded border-l-2 bg-yellow-50 border-yellow-500 text-xs text-yellow-800 space-y-1">
                <p>
                    Antes de subir tus resultados, registra la fecha de
                    presentación del examen ICFES en Conozcámonos, página 4
                    (Educación).
                </p>
                <EducationPageLink />
            </div>
        )
    }

    return (
        <div className="mb-3 p-2 rounded border border-gray-200 bg-gray-50 text-xs text-gray-700 space-y-1">
            <p>
                <span className="font-medium">Fecha de presentación:</span>{' '}
                {examDate}
            </p>
            {exam?.numeroRegistro && (
                <p>
                    <span className="font-medium">Número de registro:</span>{' '}
                    {exam.numeroRegistro}
                </p>
            )}
            <p className="text-gray-500">
                Esta información no se puede cambiar aquí. Si no es correcta,
                corrígela en Conozcámonos, página 4 (Educación).
            </p>
            <EducationPageLink />
        </div>
    )
}
